import { useEffect, useState } from "react";
import { MainLayout } from "@/components/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { BookOpen, Target, TrendingUp, Play, CheckCircle2, Award, Layers, Tag } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { authAPI, cardsAPI, evolucaoAPI, alunosAPI, categoriasAPI, modulosAPI, Categoria } from "@/lib/api";
import { isStudentPreview } from "@/lib/studentPreview";

// Renderiza o ícone da categoria: nome de ícone Lucide (ex: "BookOpen") ou fallback
const renderCategoriaIcon = (iconName: string | null | undefined, className = "w-5 h-5") => {
  if (!iconName) return <Tag className={className} />;
  const IconComponent = (LucideIcons as any)[iconName];
  return IconComponent ? <IconComponent className={className} /> : <Tag className={className} />;
};
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { StudentAnalytics } from "@/components/Analytics";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const AlunoDashboard = () => {
  const navigate = useNavigate();
  const user = authAPI.getCurrentUser();
  const previewMode = isStudentPreview();

  const [alunoProfileId, setAlunoProfileId] = useState<number | null>(null);
  const [currentModulo, setCurrentModulo] = useState<string>("");
  const [currentModuloCategoriaId, setCurrentModuloCategoriaId] = useState<number | null>(null);
  const [switchingModulo, setSwitchingModulo] = useState<string | null>(null);
  const [isSwitchingModalOpen, setIsSwitchingModalOpen] = useState(false);
  const [targetModulo, setTargetModulo] = useState<string | null>(null);
  const [targetModuloCategoriaId, setTargetModuloCategoriaId] = useState<number | null>(null);
  const [switchingStep, setSwitchingStep] = useState<"ask_video" | "confirm_without_video">("ask_video");

  const [evolucao, setEvolucao] = useState({
    cards_vistos: 0,
    qtd_facil: 0,
    qtd_medio: 0,
    qtd_dificil: 0,
    qtd_impossivel: 0,
  });
  // Os números só aparecem depois de chegarem: zero enquanto carrega parece
  // "você não estudou nada", e some um segundo depois.
  const [evolucaoCarregada, setEvolucaoCarregada] = useState(false);
  // O que há para estudar hoje. `null` = perguntando; "indisponivel" = o
  // servidor não soube dizer, e o painel volta ao convite genérico.
  const [pendentes, setPendentes] = useState<
    { revisoes: number; novos: number; modulo: string | null } | null | "indisponivel"
  >(null);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  // Categorias marcadas para estudar em sequência (ordem = ordem de clique)
  const [selectedCategoriaIds, setSelectedCategoriaIds] = useState<number[]>([]);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    if (previewMode) {
      loadPreviewData();
      return;
    }

    loadEvolucao();
    loadAnalytics();
    loadCategorias();
    cardsAPI
      .pendentes()
      .then((p) => setPendentes(p.tem_perfil ? p : "indisponivel"))
      .catch(() => setPendentes("indisponivel"));
  }, []);

  const loadPreviewData = async () => {
    try {
      const [categoriasData, modulosData] = await Promise.all([
        categoriasAPI.getAll(),
        modulosAPI.getAll(),
      ]);
      const categoriasVisiveis = (categoriasData.categorias || []).filter((categoria) => categoria.exibir_alunos);
      const categoriasPorId = new Map(categoriasVisiveis.map((categoria) => [categoria.id, categoria]));

      setCategorias(categoriasVisiveis);
      setAnalyticsData({
        modulos: (modulosData.modulos || [])
          .filter((modulo) => categoriasPorId.has(modulo.categoria_id))
          .map((modulo) => {
            const categoria = categoriasPorId.get(modulo.categoria_id)!;
            return {
              modulo_id: modulo.id,
              modulo_nome: modulo.nome,
              categoria_id: modulo.categoria_id,
              categoria_nome: categoria.nome,
              categoria_cor: categoria.cor,
              total_cards: modulo.total_cards || 0,
              cards_vistos: 0,
              concluido: 0,
            };
          }),
        diagnostico: { texto: "Demonstração sem progresso salvo.", tipo: "neutral" },
        diario: [],
        semanal: [],
        mensal: [],
      });
    } catch (error) {
      console.error("Error loading student preview:", error);
      toast.error("Erro ao carregar a demonstração do aluno.");
    } finally {
      setEvolucaoCarregada(true);
      setPendentes("indisponivel");
    }
  };

  const loadCategorias = async () => {
    try {
      const data = await categoriasAPI.getAll();
      setCategorias(data.categorias || []);
    } catch (error) {
      console.error("Error loading categorias:", error);
    }
  };

  const toggleCategoria = (id: number) => {
    setSelectedCategoriaIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const startSequence = () => {
    if (selectedCategoriaIds.length === 0) return;
    navigate(`/aluno/cards?categoria_ids=${selectedCategoriaIds.join(",")}`);
  };

  const loadAnalytics = async () => {
    if (!user) return;
    try {
      const data = await alunosAPI.getEstatisticasAvancadas();
      if (data.success) {
        setAnalyticsData(data);
      }
    } catch (error) {
      console.error("Error loading advanced analytics:", error);
    }
  };

  const loadEvolucao = async () => {
    if (!user) return;

    try {
      // 1. Obter dados do perfil do aluno para saber o modulo inicial e o ID
      const alunoData = await alunosAPI.getByUsuarioId(user.id);
      if (alunoData.has_profile && alunoData.aluno) {
        setAlunoProfileId(alunoData.aluno.id);
        setCurrentModulo(alunoData.aluno.modulo_inicial_nome || "");
        setCurrentModuloCategoriaId(alunoData.aluno.modulo_inicial_categoria_id ?? null);
      }

      // 2. Obter evolucao
      const data = await evolucaoAPI.getByUsuarioId(user.id);

      if (data.has_profile && data.estatisticas_gerais) {
        setEvolucao({
          cards_vistos: data.estatisticas_gerais.total_cards_vistos || 0,
          qtd_facil: data.estatisticas_gerais.total_facil || 0,
          qtd_medio: data.estatisticas_gerais.total_medio || 0,
          qtd_dificil: data.estatisticas_gerais.total_dificil || 0,
          qtd_impossivel: data.estatisticas_gerais.total_impossivel || 0,
        });
      }
    } catch (error) {
      console.error("Error loading evolution:", error);
      toast.error("Não foi possível carregar suas estatísticas. Confira sua internet.");
    } finally {
      setEvolucaoCarregada(true);
    }
  };

  const handleStartStudy = async (moduloNome: string, categoriaId: number) => {
    if (previewMode) {
      navigate(`/aluno/cards?categoria_id=${categoriaId}&modulo=${encodeURIComponent(moduloNome)}`);
      return;
    }

    if (currentModulo === moduloNome && currentModuloCategoriaId === categoriaId) {
      navigate("/aluno/cards");
    } else {
      if (!alunoProfileId) {
        toast.error("Erro: perfil do aluno não carregado.");
        return;
      }
      setTargetModulo(moduloNome);
      setTargetModuloCategoriaId(categoriaId);
      setSwitchingStep("ask_video");
      setIsSwitchingModalOpen(true);
    }
  };

  const confirmModuleSwitch = async () => {
    if (!targetModulo || !alunoProfileId) return;
    try {
      setSwitchingModulo(targetModulo);
      await alunosAPI.update(alunoProfileId, {
        modulo_inicial_nome: targetModulo,
        modulo_inicial_categoria_id: targetModuloCategoriaId ?? undefined
      });
      toast.success(`Módulo alterado para ${targetModulo}! Iniciando estudos...`);
      setIsSwitchingModalOpen(false);
      navigate("/aluno/cards");
    } catch (error) {
      console.error("Error switching module:", error);
      toast.error("Erro ao alterar módulo.");
    } finally {
      setSwitchingModulo(null);
    }
  };

  const statsCards = [
    {
      title: "Cards Estudados",
      value: evolucao.cards_vistos,
      icon: BookOpen,
      color: "bg-primary",
    },
    {
      title: "Fácil",
      value: evolucao.qtd_facil,
      icon: Target,
      color: "bg-facil",
    },
    {
      title: "Médio",
      value: evolucao.qtd_medio,
      icon: TrendingUp,
      color: "bg-medio",
    },
    {
      title: "Difícil",
      value: evolucao.qtd_dificil,
      icon: TrendingUp,
      color: "bg-dificil",
    },
  ];

  const groupedModulos = analyticsData?.modulos ? analyticsData.modulos.reduce((acc: any, modulo: any) => {
    const catName = modulo.categoria_nome || "Outros";
    if (!acc[catName]) {
      acc[catName] = [];
    }
    acc[catName].push(modulo);
    return acc;
  }, {}) : {};

  return (
    <MainLayout
      userType="aluno"
      userName={user?.nome || "Aluno"}
      userPhoto={user?.foto}
      userSexo="Prefiro não dizer"
    >
      <div className="space-y-8 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2">
              Olá, {user?.nome}! 👋
            </h1>
            <p className="text-muted-foreground">
              Continue seu aprendizado e alcance seus objetivos!
            </p>
          </div>
        </div>

        {/* O que há para hoje: é a primeira coisa que o aluno quer saber. */}
        <Card className="shadow-lg border-none bg-gradient-hero text-white" data-testid="para-hoje">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="space-y-2 min-w-0">
                {pendentes === null ? (
                  <>
                    <h2 className="text-2xl font-bold">Vendo o que há para hoje…</h2>
                    <p className="text-white/90">Um instante.</p>
                  </>
                ) : pendentes === "indisponivel" ? (
                  <>
                    <h2 className="text-2xl font-bold">Continue de onde parou!</h2>
                    <p className="text-white/90">
                      Você está indo muito bem! Continue praticando para melhorar ainda mais.
                    </p>
                  </>
                ) : pendentes.revisoes + pendentes.novos === 0 ? (
                  <>
                    <h2 className="text-2xl font-bold">Revisões em dia! 🎉</h2>
                    <p className="text-white/90">
                      Nada vencido{pendentes.modulo ? ` em ${pendentes.modulo}` : ""} por hoje. Volte amanhã — ou
                      escolha uma categoria ou outro módulo aqui embaixo.
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold">
                      {pendentes.revisoes + pendentes.novos === 1
                        ? "1 card espera por você hoje"
                        : `${pendentes.revisoes + pendentes.novos} cards esperam por você hoje`}
                    </h2>
                    <p className="text-white/90">
                      {[
                        pendentes.revisoes ? `${pendentes.revisoes} para rever` : "",
                        pendentes.novos ? `${pendentes.novos} ${pendentes.novos === 1 ? "novo" : "novos"}` : "",
                      ]
                        .filter(Boolean)
                        .join(" e ")}
                      {pendentes.modulo ? ` em ${pendentes.modulo}` : ""}. Pode parar quando quiser: o que você
                      responder fica gravado.
                    </p>
                  </>
                )}
              </div>
              <Button
                onClick={() => navigate("/aluno/cards")}
                size="lg"
                variant="secondary"
                className="shadow-xl w-full sm:w-auto shrink-0"
              >
                {typeof pendentes === "object" && pendentes && pendentes.revisoes + pendentes.novos > 0
                  ? "Estudar agora"
                  : "Continuar"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {statsCards.map((stat, index) => (
            <Card
              key={index}
              className="overflow-hidden hover:shadow-lg transition-all duration-300 border-none shadow-md animate-scale-in"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <div className={`${stat.color} p-2 rounded-lg`}>
                  <stat.icon className="w-4 h-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-foreground tabular-nums">
                  {evolucaoCarregada ? stat.value : <span className="text-muted-foreground" aria-label="carregando">…</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Seção de Estudo por Categoria */}
        {categorias.length > 0 && (
          <div className="space-y-4 animate-scale-in">
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Layers className="w-6 h-6 text-primary" /> Estudar por Categoria
            </h2>
            <p className="text-sm text-muted-foreground -mt-2">
              Marque uma ou mais categorias para estudar em sequência, independente do módulo.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categorias.map((cat) => {
                const selected = selectedCategoriaIds.includes(cat.id);
                return (
                  <Card
                    key={cat.id}
                    role="checkbox"
                    aria-checked={selected}
                    className={`border-none shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer ${
                      selected ? "ring-2 ring-primary" : ""
                    }`}
                    onClick={() => toggleCategoria(cat.id)}
                    style={{ borderLeft: `6px solid ${cat.cor || "#013079"}` }}
                  >
                    <CardContent className="p-5 flex items-center gap-4">
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => toggleCategoria(cat.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="shrink-0"
                        aria-label={`Selecionar categoria ${cat.nome}`}
                      />
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${cat.cor || "#013079"}20`, color: cat.cor || "#013079" }}
                      >
                        {renderCategoriaIcon(cat.icone)}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-foreground truncate">{cat.nome}</h3>
                        {cat.descricao && (
                          <p className="text-xs text-muted-foreground truncate">{cat.descricao}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {selectedCategoriaIds.length > 0 && (
              <div className="flex justify-end">
                <Button
                  onClick={startSequence}
                  size="lg"
                  className="bg-primary hover:bg-primary-dark shadow-lg hover:shadow-xl transition-all"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Começar a Estudar ({selectedCategoriaIds.length})
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Seção de Módulos (Menu de Revisão e Seleção) */}
        {analyticsData?.modulos && analyticsData.modulos.length > 0 && (
          <div className="space-y-4 animate-scale-in">
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Award className="w-6 h-6 text-primary animate-pulse" /> Módulos de Estudo e Revisão
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.keys(groupedModulos).map((categoryName) => {
                const modules = groupedModulos[categoryName];
                const categoryColor = modules[0]?.categoria_cor || "#013079";
                return (
                  <Card key={categoryName} className="border-none shadow-md overflow-hidden hover:shadow-lg transition-all duration-300">
                    <CardHeader className="pb-3 border-b border-muted/50" style={{ borderLeft: `6px solid ${categoryColor}` }}>
                      <CardTitle className="text-lg font-bold flex items-center justify-between">
                        <span>{categoryName}</span>
                        <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-1 rounded-full">
                          {modules.length} {modules.length === 1 ? 'módulo' : 'módulos'}
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 divide-y divide-muted/50">
                      {modules.map((modulo: any) => {
                        // Nome E categoria: módulos de categorias diferentes podem
                        // ter o mesmo nome, e os dois apareciam como "Estudando".
                        const isCurrent =
                          currentModulo === modulo.modulo_nome &&
                          (currentModuloCategoriaId == null || currentModuloCategoriaId === modulo.categoria_id);
                        const isCompleted = modulo.concluido === 1 || modulo.cards_vistos >= modulo.total_cards;
                        const progressPercent = modulo.total_cards > 0 ? (modulo.cards_vistos / modulo.total_cards) * 100 : 0;
                        return (
                          <div key={modulo.modulo_id} className={`p-4 transition-all hover:bg-muted/5 ${isCurrent ? 'bg-primary/5' : ''}`}>
                            <div className="flex items-start justify-between gap-4 mb-2">
                              <div>
                                <h3 className="font-semibold text-foreground flex items-center gap-2 text-sm sm:text-base">
                                  {modulo.modulo_nome}
                                  {isCompleted && (
                                    <CheckCircle2 className="w-4 h-4 text-emerald-500 fill-emerald-50 shrink-0" />
                                  )}
                                </h3>
                                <p className="text-xs text-muted-foreground">
                                  {modulo.cards_vistos} de {modulo.total_cards} cards estudados
                                </p>
                              </div>
                              <div className="shrink-0">
                                {isCurrent ? (
                                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                                    Estudando
                                  </span>
                                ) : isCompleted ? (
                                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    Concluído
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            
                            {/* Barra de Progresso */}
                            <div className="flex items-center gap-3 mt-3">
                              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className="h-full rounded-full transition-all duration-500" 
                                  style={{ 
                                    width: `${progressPercent}%`,
                                    backgroundColor: isCompleted ? '#10b981' : categoryColor 
                                  }}
                                />
                              </div>
                              <span className="text-xs font-medium text-muted-foreground w-8 text-right shrink-0">
                                {Math.round(progressPercent)}%
                              </span>
                            </div>

                            {/* Botão de Estudar / Revisar */}
                            <div className="flex justify-end mt-4">
                              <Button
                                size="sm"
                                variant={isCurrent ? "default" : "outline"}
                                className={`w-full sm:w-auto font-medium transition-all ${
                                  isCurrent ? 'bg-primary hover:bg-primary/95 text-white shadow-md' : 'hover:bg-primary/5'
                                }`}
                                disabled={switchingModulo === modulo.modulo_nome}
                                onClick={() => handleStartStudy(modulo.modulo_nome, modulo.categoria_id)}
                              >
                                <Play className="w-3.5 h-3.5 mr-1.5 fill-current" />
                                {switchingModulo === modulo.modulo_nome 
                                  ? 'Carregando...' 
                                  : isCurrent 
                                    ? 'Continuar Módulo' 
                                    : isCompleted 
                                      ? 'Revisar Módulo' 
                                      : 'Estudar Módulo'}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        <Card className="shadow-md">
          <CardHeader>
            <CardTitle>Progresso por Dificuldade</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                { label: "Fácil", value: evolucao.qtd_facil, color: "bg-facil" },
                { label: "Médio", value: evolucao.qtd_medio, color: "bg-medio" },
                { label: "Difícil", value: evolucao.qtd_dificil, color: "bg-dificil" },
                { label: "Impossível", value: evolucao.qtd_impossivel, color: "bg-impossivel" },
              ].map((item) => {
                const total = evolucao.cards_vistos || 1;
                const percentage = (item.value / total) * 100;

                return (
                  <div key={item.label} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{item.label}</span>
                      <span className="text-muted-foreground">{item.value} cards</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full ${item.color} transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {analyticsData && (
          <StudentAnalytics
            modulosData={analyticsData.modulos}
            diagnostico={analyticsData.diagnostico}
            diarioData={analyticsData.diario}
            semanalData={analyticsData.semanal}
            mensalData={analyticsData.mensal}
          />
        )}

        {/* Modal de confirmação ao trocar módulo manualmente */}
        {targetModulo && (
          <AlertDialog open={isSwitchingModalOpen} onOpenChange={setIsSwitchingModalOpen}>
            <AlertDialogContent className="max-w-md border-none shadow-2xl bg-card">
              {switchingStep === "ask_video" && (
                <>
                  <AlertDialogHeader className="space-y-3">
                    <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary text-2xl">
                      📺
                    </div>
                    <AlertDialogTitle className="text-center text-2xl font-bold text-foreground">
                      Aula de Vocabulário
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-center text-muted-foreground text-base">
                      Você assistiu à aula de vocabulário do módulo <strong>{targetModulo}</strong>?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter className="flex sm:flex-row gap-2 mt-4 justify-center">
                    <Button
                      variant="outline"
                      onClick={() => setSwitchingStep("confirm_without_video")}
                      className="w-full sm:w-auto"
                    >
                      Não
                    </Button>
                    <Button
                      onClick={confirmModuleSwitch}
                      className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white"
                      disabled={switchingModulo !== null}
                    >
                      {switchingModulo ? "Carregando..." : "Sim, assisti!"}
                    </Button>
                  </AlertDialogFooter>
                </>
              )}

              {switchingStep === "confirm_without_video" && (
                <>
                  <AlertDialogHeader className="space-y-3">
                    <div className="mx-auto w-12 h-12 bg-amber-500/10 rounded-full flex items-center justify-center text-amber-500 text-2xl animate-pulse">
                      ⚠️
                    </div>
                    <AlertDialogTitle className="text-center text-xl font-bold text-foreground">
                      Tem certeza?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-center text-muted-foreground text-base">
                      Tem certeza de que quer liberar o módulo <strong>{targetModulo}</strong> sem assistir à aula de vocabulário primeiro?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter className="flex sm:flex-row gap-2 mt-4 justify-center">
                    <Button
                      variant="outline"
                      onClick={() => setIsSwitchingModalOpen(false)}
                      className="w-full sm:w-auto"
                    >
                      Não, ver aula primeiro
                    </Button>
                    <Button
                      onClick={confirmModuleSwitch}
                      className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white"
                      disabled={switchingModulo !== null}
                    >
                      {switchingModulo ? "Carregando..." : "Sim, liberar módulo"}
                    </Button>
                  </AlertDialogFooter>
                </>
              )}
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </MainLayout>
  );
};

export default AlunoDashboard;
